<template>
  <div>
    <div class="select-form">
      <div class="select-row">
        <div class="select-label" v-if="inputLabel === undefined || inputLabel">{{inputLabel || '已选择'}}</div>
        <a-select
          v-model:value="selected"
          :open="false"
          :options="selected"
          class="select"
          label-in-value
          mode="tags"
        />
      </div>
    </div>
    <widget-search
      :form="form"
      :groups="groups"
      @search="onSearch"
      :show="showSearch"
    />
    <widget-table-button :buttons="buttons">
      <widget-search-btn v-model="showSearch" />
    </widget-table-button>
    <widget-table-body
      :columns="columns"
      :list-data="listData"
      :select-row-key="schema.primary"
      :row-selection="rowSelection"
      selectable
    />
    <!--    分页    -->
    <widget-table-pagination
      v-model:page-size="service.listConfig.pageSize"
      v-model:page-num="service.listConfig.pageNum"
      :count="listData.count"
    />
  </div>
</template>
<script lang="ts" setup>
import { computed, ref } from 'vue';
import WidgetSearch from '@/components/business/crud/form/WidgetSearch.vue';
import WidgetTableBody from '@/components/business/crud/table/WidgetTableBody.vue';
import WidgetTableButton from '@/components/business/crud/table/WidgetTableButton.vue';
import WidgetTablePagination from '@/components/business/crud/table/WidgetTablePagination.vue';
import { createSearchService } from '@/components/business/crud/service/CrudSearchService';
import { createCrudTableService } from '@/components/business/crud/service/CrudTableService';
import WidgetSearchBtn from '@/components/business/crud/form/WidgetSearchBtn.vue';

const props = defineProps<{
  moduleName: string;
  controllerName: string;
  labelName: string;
  condition?: any;
  inputLabel?: string;
  hiddenFields?: string[]; // 隐藏字段
  modelValue: any[];
}>();

const emit = defineEmits<{
  (event: 'update:modelValue', data: any[]): void;
}>();

const showSearch = ref(false);
const searchService = createSearchService(
  props.moduleName,
  props.controllerName
);
const { form, groups, getCondition } = searchService.use();

const service = createCrudTableService(props.moduleName, props.controllerName);
service.listConfig.pageSize = 10;
if (props.hiddenFields) {
  service.widgetContainer.hiddenFields(...props.hiddenFields);
}
const { schema, columns, listData, buttons } = service.use();

function onSearch() {
  if (props.condition) {
    service.listConfig.condition = {
      $and: [props.condition, getCondition()],
    };
  } else {
    service.listConfig.condition = getCondition();
  }
}

searchService.init().then(() => {
  onSearch();
  service.init();
  service.widgetContainer.hideButtonsExclude('view', 'refresh');
});

/**
 * 计算新的modelValue值
 * @param modelValue 当前的modelValue值，既历史已选择的值
 * @param currentPageData 当前页面的数据
 * @param currentPageSelectedData 当前页面已选择的数据
 */
function computeNewModelValue(
  modelValue: any[],
  currentPageData: any[],
  currentPageSelectedData: any[]
): any[] {
  // 创建当前页面ID集合
  const currentPageSet = new Set(currentPageData.map(getDataKey));

  // 创建当前页面选中数据的Map
  const currentPageSelectedMap = new Map(
    currentPageSelectedData.map((item) => [getDataKey(item), item])
  );

  // 用于存储新的modelValue
  const newModelValue: any[] = [];
  // 遍历历史modelValue
  for (const item of modelValue) {

    if (!currentPageSet.has(getDataKey(item))) {
      // 不在当前页面的数据，直接保留
      newModelValue.push(item);
    } else if (currentPageSelectedMap.has(getDataKey(item))) {
      // 在当前页面且已选中的数据，保留并从currentPageSelectedMap移除
      newModelValue.push(item);
      currentPageSelectedMap.delete(getDataKey(item));
    }
  }
  // 将currentPageSelectedMap中剩余的选中数据添加到newModelValue
  newModelValue.push(...currentPageSelectedMap.values());
  return newModelValue;
}

function getDataKey(data: any) {
  return data[schema.primary];
}

const rowSelection = {
  fixed: true,
  selectedRowKeys: computed(() =>
    props.modelValue.map((i) => i[schema.primary])
  ),
  onChange: (_: any[], rows: any[]) => {
    emit(
      'update:modelValue',
      computeNewModelValue(props.modelValue, listData.list, rows)
    );
  },
};

const selected = computed({
  get: () =>
    props.modelValue.map((i) => {
      return {
        label: i[props.labelName],
        value: i[schema.primary],
      };
    }),
  set: (value) => {
    const selectedSet = new Set(value.map((i: any) => i.value));
    emit(
      'update:modelValue',
      props.modelValue.filter((i: any) => selectedSet.has(i[schema.primary]))
    );
  },
});
</script>

<style lang="scss" scoped>
.index-box {
  flex-grow: 1;
  overflow: auto;
  padding: 20px;
}

.select-form {
  margin-bottom: $spacing-medium;

  .select-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: $spacing-medium;

    .select-label {
      margin-right: $spacing-medium;
    }

    .select {
      width: 80%;
    }
  }
}
</style>
